import { Immutable } from "immer";
import React from "react";

import type { DetailSliderBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getSpacingClasses } from "../spacing";

import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

type Slide = NonNullable<Value["slides"]>[number];
type SlideStream = NonNullable<Slide["slide"]>;
type SlideItem = SlideStream[number];

/**
 * Faithful port of the legacy server-rendered `detail-slider-block.html`. The
 * nav-header click interactivity is owned here via `activeSlide` state: clicking
 * a nav header selects its slide, and the selected index drives the
 * `isCurrent` highlight on both the matching `subBlockNav` header and its
 * `subBlock` pane (the first slide is selected by default, matching the legacy
 * `{% if forloop.first %}` render).
 */

/**
 * Reproduce the per-child markup the legacy `slide` StreamBlock emitted. Wagtail
 * wraps each child block in `<div class="w-block-{type} block-{type}">`; the
 * `sliderContent` SCSS (`> .block-image img { width: 100%; object-fit:
 * cover }`, `> * { flex: 1 }`) depends on those wrappers, so they are
 * load-bearing for the screenshot gate -- not decorative. thelabui's
 * `StreamField` does not emit these wrappers, so render them inline here.
 */
const renderSlideItem = (item: Immutable<SlideItem>): React.JSX.Element => {
    const className = `w-block-${item.type} block-${item.type}`;
    if (item.type === "image") {
        const img = item.value;
        // The legacy image used `{% wagtail_image ... specs='width-250,
        // width-500,width-1000,width-2000' set_max_dimensions=True %}`. The
        // baseline fragment's slide `<img>` carries no class (just `src` +
        // the `set_max_dimensions` inline style), so emit `width-2000`
        // (the largest rendition; Wagtail never upscales, so it clamps to the
        // source width) and reproduce the `max-width`/`max-height` style. The
        // `width-2000` rendition is declared on the Python block, so the index
        // access is always defined.
        return (
            <div className={className}>
                <img
                    alt={img.title}
                    src={img.renditions["width-2000"].url}
                    loading="lazy"
                    decoding="async"
                    style={{
                        maxWidth: `min(100%, ${img.width}px)`,
                        maxHeight: `${img.height}px`,
                    }}
                />
            </div>
        );
    }
    // The `rich_text` variant's value is a non-null `RichTextBlock` (string).
    return (
        <div className={className}>
            <RichTextBlock value={item.value} />
        </div>
    );
};

export const DetailSliderBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const slides = v.slides ?? [];
    const [activeSlide, setActiveSlide] = React.useState(0);
    return (
        <section
            className={concatClassNames([
                styles.detailSlider,
                getSpacingClasses(v.spacing),
            ])}
        >
            {/* Legacy intro was `{% include_block value.intro %}`, which emitted
                the serialized rich-text HTML (`<div class="rich-text"><p>…</p>
                </div>`) as the DIRECT child of `.detailSlider` -- so the
                `.detailSlider > div.rich-text { padding: 1.5vw 5vw 0 }` rule
                fired, giving the intro its narrow column. Wrapping the value in
                `<RichTextBlock>` (thelabui) adds an extra plain `<div>`,
                demoting `.rich-text` to a grandchild, dropping that padding and
                widening the intro by one wrapped line (~21px taller baseline).
                Instead make the host element itself the `.rich-text` div (the
                serialized value already nests an inner `.rich-text`; the global
                `.rich-text` rule is `display:flex` only, no box-model padding,
                so the double-nest is layout-neutral) -- same idiom as
                OfferBannerBlock -- so it is again a direct `div.rich-text`
                child. */}
            <div
                className="rich-text"
                dangerouslySetInnerHTML={{ __html: v.intro ?? "" }}
            />
            <div>
                {/* NAV headers. Index key: the outer `slides` ListBlock exposes
                    no stable id, matching the legacy `forloop`-positional
                    render. `isCurrent` marks the selected slide's nav. */}
                {slides.map((slide, i) => {
                    const icon = slide.icon;
                    return (
                        <div
                            key={i}
                            className={concatClassNames([
                                styles.subBlockNav,
                                i === activeSlide ? styles.isCurrent : null,
                            ])}
                        >
                            <header
                                onClick={(event) => {
                                    event.preventDefault();
                                    setActiveSlide(i);
                                }}
                            >
                                {/* Legacy: `{% image block.icon fill-35x35 as
                                    iconItem %}` then `<img alt="{{ iconItem.alt
                                    }}" src="{{ iconItem.url }}" />`. The Wagtail
                                    `{% image %}` tag's `.alt` defaults to the
                                    image's `default_alt_text`. Guard the
                                    nullable icon. */}
                                {icon ? (
                                    <img
                                        alt={icon.default_alt_text}
                                        src={icon.renditions["fill-35x35"].url}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : null}
                                <h4>{slide.title}</h4>
                                {slide.subtitle ? (
                                    <p className="caption">{slide.subtitle}</p>
                                ) : null}
                            </header>
                        </div>
                    );
                })}
                {/* CONTENT panes. Second loop over the same `slides`, matching
                    the legacy two-loop structure; `isCurrent` reveals the
                    selected slide's pane. */}
                {slides.map((slide, i) => (
                    <div
                        key={i}
                        className={concatClassNames([
                            styles.subBlock,
                            i === activeSlide ? styles.isCurrent : null,
                        ])}
                    >
                        <div className={styles.sliderContent}>
                            {(slide.slide ?? []).map((item) => (
                                // Slide StreamBlock children carry stable ids.
                                <React.Fragment key={item.id}>
                                    {renderSlideItem(item)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};
